/**
 * Whether any coordinated surface (overlay-system-design §6.6) owns the screen or is about to, a way
 * to hear when that may have changed, and a once-idle wait. For something that is not a surface
 * itself and must not land over one: the match-arrival banner, whose tap navigates.
 *
 * Built on the mutex (its grants and its takeover park) and the intent channel alone. No surface is
 * named here, so one added later is covered once it takes a grant, including one a closing surface
 * opens from its own continuations: the wait reports idle one task after the last surface closes.
 * The route-level surfaces (a Space's PIN drawer, the create-event flow) are covered too; they
 * unmount on a navigation, so waiting for them is not needed, only harmless.
 */
import { getIntents, subscribeIntents } from './intents';
import { getOverlayMutexSnapshot, subscribeOverlayMutex } from './mutex';

const listeners = new Set<() => void>();

// Every transition of the intent channel or the mutex, forwarded to subscribers, who re-read the
// predicate they care about on each one rather than inspecting the notify itself.
const notify = (): void => {
  listeners.forEach(l => {
    l();
  });
};
subscribeIntents(notify);
subscribeOverlayMutex(notify);

/** Subscribes to every intent and mutex transition. Returns unsubscribe. */
export function onCoordinatedSurfacesChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * True while a celebration is posted and not yet acquired (its host claims it at cold start, before
 * anything holds the mutex), any surface holds a mutex grant or waits for one, or a takeover parks
 * the mutex.
 *
 * Queued counts as well as held. Dismissing the holder notifies with no holder before the next
 * waiter is granted (§6.2 rule 3), so a reader of the holder alone would see the screen free in
 * that gap, under the incoming sheet.
 *
 * Takeovers never take a grant (§6.4), but they own the screen all the same: the session drawers
 * sit under the toast layer, so a banner over one would navigate behind it, and maintenance and the
 * chunk-update overlay sit over it, so a banner there would time out unseen.
 */
export function isAnyCoordinatedSurfaceBusy(): boolean {
  if (getIntents().some(intent => intent.surface === 'celebration')) return true;
  const { holderId, queuedIds, takeoverActive } = getOverlayMutexSnapshot();
  return holderId !== null || queuedIds.length > 0 || takeoverActive;
}

/**
 * True while a coordinated surface is on the screen or about to be: a surface holds a mutex grant,
 * or a takeover parks the mutex. For taking down something already showing, where
 * `isAnyCoordinatedSurfaceBusy` is too wide: a request still in line can settle stale at its turn,
 * and a celebration's host can ack its intent the moment it is posted (its once-ever mark spent),
 * and neither ever renders. Nothing is lost by waiting for the grant: the mutex sets the holder and
 * notifies inside the call that grants it (mutex.ts `step`), before the `acquire` continuation that
 * writes the surface's open-state can run.
 */
export function isAnyCoordinatedSurfaceOnScreen(): boolean {
  const { holderId, takeoverActive } = getOverlayMutexSnapshot();
  return holderId !== null || takeoverActive;
}

/**
 * Resolves true once `isBusy` reads false, at once or on a later notify from `subscribe`; false if
 * `signal` aborts first. Never rejects. A `true` can still reach a caller whose signal aborted after
 * resolution; check `signal.aborted` after the await.
 */
export function awaitIdle(
  isBusy: () => boolean,
  subscribe: (listener: () => void) => () => void,
  signal: AbortSignal,
): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise<boolean>(resolve => {
    const onAbort = () => {
      unsubscribe();
      resolve(false);
    };
    const check = () => {
      if (isBusy()) return;
      unsubscribe();
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    };
    const unsubscribe = subscribe(check);
    signal.addEventListener('abort', onAbort, { once: true });
    check();
  });
}

/**
 * Resolves true once no coordinated surface holds or waits for the screen
 * (`isAnyCoordinatedSurfaceBusy`), one task after the last one closed; false if `signal` aborts
 * first. Never rejects; check `signal.aborted` after the await, as for `awaitIdle`.
 */
export function awaitCoordinatedSurfacesIdle(signal: AbortSignal): Promise<boolean> {
  return awaitIdle(isAnyCoordinatedSurfaceBusy, onCoordinatedSurfacesChange, signal).then(idle =>
    idle ? afterClosingTask(signal) : false,
  );
}

/**
 * Idle is first reported inside the closing surface's dismiss, before that dismiss's own
 * continuations run: CelebrationHost opens the first-Going carousel from a grant's `closed`, and a
 * prompt waiting on `awaitCelebrationIdle` asks for its turn from its `.then`. One task later those
 * have run. Still idle resolves true; a surface they opened sends the wait round again.
 */
function afterClosingTask(signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise<boolean>(resolve => {
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(isAnyCoordinatedSurfaceBusy() ? awaitCoordinatedSurfacesIdle(signal) : true);
    }, 0);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
